import { useCallback, useMemo } from "react";
import { useScheduleContext } from "../context/useScheduleContext";

// Collapse state is derived from `resources` rather than kept per group: a group is collapsed
// when none of its members are present (every group has at least one member). This keeps the
// row chevrons and the corner "toggle all" in sync, and resets naturally when data is refetched.
export const useGroupCollapse = () => {
    const { dispatch, resources, allResources } = useScheduleContext();

    const groupIds = useMemo(() => {
        return allResources
            .filter((res: any) => res.isHeader)
            .map((res: any) => res.originalId);
    }, [allResources]);

    const collapsedIds = useMemo(() => {
        const visible = new Set(
            resources
                .filter((res: any) => !res.isHeader)
                .map((res: any) => res.originalId)
        );
        return new Set(groupIds.filter((id: any) => !visible.has(id)));
    }, [resources, groupIds]);

    const applyCollapsed = useCallback((collapsed: Set<any>) => {
        const next = allResources.filter(
            (res: any) => res.isHeader || !collapsed.has(res.originalId)
        );
        dispatch({ type: "set-resources", payload: next });
    }, [allResources, dispatch]);

    const toggleGroup = useCallback((groupId: any) => {
        const next = new Set(collapsedIds);
        if (next.has(groupId)) next.delete(groupId);
        else next.add(groupId);
        applyCollapsed(next);
    }, [collapsedIds, applyCollapsed]);

    const allCollapsed = groupIds.length > 0 && collapsedIds.size === groupIds.length;

    const toggleAll = useCallback(() => {
        applyCollapsed(allCollapsed ? new Set() : new Set(groupIds));
    }, [allCollapsed, groupIds, applyCollapsed]);

    return {
        hasGroups: groupIds.length > 0,
        isCollapsed: (groupId: any) => collapsedIds.has(groupId),
        allCollapsed,
        toggleGroup,
        toggleAll
    };
};
